import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const navLinks = [
  { label: "ABOUT US", href: "/about", internal: true },
  { label: "HEATING", href: "/heating", internal: true },
  { label: "COOLING", href: "/cooling", internal: true },
  { label: "GALLERY", href: "/gallery", internal: true },
  { label: "CONTACT", href: "/contact", internal: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    const el = document.querySelector(href);
    if (!el) return;
    const navbarHeight = 148;
    const top = el.getBoundingClientRect().top + window.scrollY - navbarHeight;
    window.scrollTo({ top, behavior: "smooth" });
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname === "/") {
      const el = document.getElementById("contact");
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: "smooth" });
      }
    } else {
      sessionStorage.setItem("scrollTarget", "contact");
      navigate("/");
    }
    setMenuOpen(false);
  };

  return (
    <>
      {/* Announcement bar */}
      <div className="bg-[#0B2447] text-white text-xs py-2 overflow-hidden">
        <div className="flex items-center justify-center gap-4 px-4 flex-wrap">
          {/* Mobile: show only emergency + free estimates */}
          <span className="flex items-center gap-1.5">
            <i className="ri-shield-check-line text-[#C8102E]"></i>
            Emergency Service Available
          </span>
          <span className="text-white/30 hidden sm:inline">|</span>
          <span className="hidden sm:flex items-center gap-1.5">
            <i className="ri-fire-line text-[#C8102E]"></i>
            Now Hiring Certified HVAC Technicians
          </span>
          <span className="text-white/30 hidden sm:inline">|</span>
          <a href="#contact" onClick={handleContactClick} className="underline hover:text-[#C8102E] transition-colors hidden sm:inline">Free Estimates</a>
          <a href="#contact" onClick={handleContactClick} className="underline hover:text-[#C8102E] transition-colors sm:hidden text-[#C8102E] font-semibold">Free Estimate →</a>
        </div>
      </div>

      {/* Main header */}
      <header className={`w-full z-50 transition-all duration-300 ${scrolled ? "fixed top-0 left-0 shadow-sm bg-white border-b border-gray-100" : "relative bg-white"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://storage.readdy-site.link/project_files/f6c24b4f-84f4-4d51-a5a2-519060acd333/1130a0b4-0224-45ca-8129-e1e93596765b_ae8cdb_d4f5514df0464e0798f081e59d1aaa97mv2.png?v=60ca6d9e8a7483394fd5de472425117d"
              alt="Ultimate Heating & Cooling"
              className="h-11 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Phone */}
          <a href="tel:9592418524" className="hidden md:flex items-center gap-2 text-[#0B2447] font-bold text-xl hover:text-[#C8102E] transition-colors cursor-pointer">
            <span className="w-8 h-8 flex items-center justify-center bg-[#C8102E]/10 rounded-full">
              <i className="ri-phone-fill text-[#C8102E] text-base"></i>
            </span>
            (959)241-8524
          </a>

          {/* Quick actions */}
          <div className="hidden md:flex items-center gap-2">
            <button onClick={handleContactClick} className="text-[#0B2447] text-xs font-bold px-5 py-2.5 rounded-full border border-gray-200 hover:border-[#C8102E] transition-colors cursor-pointer whitespace-nowrap">
              SCHEDULE SERVICE
            </button>
            <button onClick={handleContactClick} className="bg-[#C8102E] text-white text-xs font-bold px-5 py-2.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap">
              GET FREE QUOTE
            </button>
          </div>

          {/* Mobile right side: phone icon + hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <a
              href="tel:9592418524"
              className="w-9 h-9 flex items-center justify-center bg-[#C8102E]/10 rounded-full cursor-pointer"
              aria-label="Call us"
            >
              <i className="ri-phone-fill text-[#C8102E] text-base"></i>
            </a>
            <button
              className="w-9 h-9 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl text-[#0B2447] ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Nav strip */}
        <nav className="bg-[#0B2447] hidden md:block">
          <div className="max-w-7xl mx-auto px-6">
            <ul className="flex items-center">
              {navLinks.map((link) => (
                <li key={link.label}>
                  {link.internal ? (
                    <Link
                      to={link.href}
                      className="block px-4 py-3 text-[11px] font-semibold text-gray-200 hover:text-[#C8102E] hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap tracking-widest"
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="block px-4 py-3 text-[11px] font-semibold text-gray-200 hover:text-[#C8102E] hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap tracking-widest"
                      onClick={(e) => scrollToSection(e, link.href)}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {/* Mobile menu */}
        {menuOpen && (
          <nav className="md:hidden bg-[#0B2447]">
            {/* CTA buttons at top of mobile menu */}
            <div className="px-4 py-3 flex gap-2 border-b border-white/10">
              <button
                onClick={handleContactClick}
                className="flex-1 text-center bg-[#C8102E] text-white text-xs font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                FREE QUOTE
              </button>
              <a
                href="tel:9592418524"
                className="flex-1 text-center border border-white/30 text-white text-xs font-bold py-2.5 rounded-full cursor-pointer whitespace-nowrap"
              >
                (959)241-8524
              </a>
            </div>
            <ul className="flex flex-col">
              {navLinks.map((link) => (
                <li key={link.label}>
                  {link.internal ? (
                    <Link
                      to={link.href}
                      className="flex items-center px-5 py-3.5 text-sm font-semibold text-gray-200 hover:text-[#C8102E] hover:bg-white/5 cursor-pointer border-b border-white/5"
                      onClick={() => setMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="flex items-center px-5 py-3.5 text-sm font-semibold text-gray-200 hover:text-[#C8102E] hover:bg-white/5 cursor-pointer border-b border-white/5"
                      onClick={() => setMenuOpen(false)}
                    >
                      {link.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        )}
      </header>
    </>
  );
}
